<template>
  <b-card
    no-body
  >
    <div
      v-if="tableTitle"
      class="m-2 mb-0"
    >
      <h2 class="content-header-title mb-0">
        {{ $t(tableTitle) }}
      </h2>
    </div>

    <div
      v-if="showTableTop"
      class="m-2"
    >

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0 pl-0"
        >
          <v-select
            v-model="perPage"
            :options="perPageOptions"
            :clearable="false"
            class="per-page-selector d-inline-block ml-50 mr-1"
          />
          <slot name="create-button" />
        </b-col>

        <!-- Search -->
        <b-col
          v-if="showSearch || filterClass || $slots['before-search']"
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-end">
            <slot name="before-search" />
            <b-form-input
              v-if="showSearch"
              v-model="searchQuery"
              class="d-inline-block mr-1"
              :placeholder="$t(searchPlaceholder)"
              @input="onSearch"
            />
            <b-button
              v-if="filterClass"
              variant="outline-primary"
              class="btn-icon mr-1"
              @click="toggleFilter()"
            >
              <feather-icon
                icon="FilterIcon"
                size="16"
              />
            </b-button>
          </div>
        </b-col>
      </b-row>
      <b-row
        v-if="filterClass"
      >
        <b-col md="12">
          <component
            :is="`${moduleName}FilterComponent`"
            :filter-toggle="filterToggle"
            :filters="filters"
            :filter-class="filterClass"
            :selected-filters="selectedFilters"
            @searchQueryClear="searchQueryClear"
            @onFilter="onFilter"
            @onGenerateExel="onGenerateExel"
            @onRemoveSelectedFilter="reloadData"
          />
        </b-col>
      </b-row>
    </div>
    <b-table
      :sort-by.sync="sortBy"
      :sort-desc.sync="isSortDirDesc"
      :per-page="perPage"
      :current-page="currentPage"
      v-bind="getTableProps()"
      v-on="tableListeners"
    >
      <template
        v-for="cell in cellSlotNames"
        #[cell]="data"
      >
        <slot
          :name="cell"
          :data="data"
        >
          {{ data.value !== null && data.value !== undefined && data.value !== '' ? data.value: "&mdash;" }}
        </slot>
      </template>
      <template #cell(index)="data">
        {{ (currentPage - 1) * perPage + data.index + 1 }}
      </template>
      <template
        v-for="head in headSlotNames"
        #[head]="data"
      >
        <slot
          :name="head"
          :data="data"
        >
          {{ $t(data.label) }}
        </slot>
      </template>

      <template #table-busy>
        <slot name="table-busy">
          <div class="text-center text-danger my-2">
            <b-spinner
              variant="primary"
              label="Text Centered"
            />
          </div>
        </slot>
      </template>
    </b-table>
    <div class="mx-2 mb-2">
      <b-row>
        <b-col
          cols="12"
          sm="6"
          class="d-flex align-items-center justify-content-center justify-content-sm-start"
        >
          <span class="text-muted">{{ $t("table summary", { from: dataMeta.from, to: dataMeta.to, of: dataMeta.of }) }} </span>
        </b-col>
        <!-- Pagination -->
        <b-col
          cols="12"
          sm="6"
          class="d-flex align-items-center justify-content-center justify-content-sm-end"
        >
          <b-pagination
            v-if="isPagination"
            v-model="currentPage"
            :total-rows="total"
            :per-page="perPage"
            first-number
            last-number
            class="mb-0 mt-1 mt-sm-0"
            prev-class="prev-item"
            next-class="next-item"
          >
            <template #prev-text>
              <feather-icon
                icon="ChevronLeftIcon"
                size="18"
              />
            </template>
            <template #next-text>
              <feather-icon
                icon="ChevronRightIcon"
                size="18"
              />
            </template>
          </b-pagination>
        </b-col>
      </b-row>
      <slot name="create-body" />
    </div>
  </b-card>
</template>

<script>

import {
  BTable, BRow, BCol,
  BPagination, BSpinner, VBTooltip, BCard, BFormInput, BButton,
} from 'bootstrap-vue'
import Vue from 'vue'
import vSelect from 'vue-select'
import { debounce } from '@core/utils/utils'
import tableConfig from '@/libs/tableConfig'
import axios from '@/libs/axios'

export default {
  name: 'LTable',
  components: {
    BButton,
    BFormInput,
    BCard,
    vSelect,
    BTable,
    BRow,
    BCol,
    BPagination,
    BSpinner,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    tableConfigOptions: {
      type: Object,
      default: () => ({}),
    },
    tableColumns: {
      type: Array,
      required: true,
    },
    moduleName: {
      type: String,
      required: true,
    },
    tableProps: {
      type: Object,
      default: () => ({}),
    },
    tableListeners: {
      type: Object,
      default: () => ({}),
    },
    filterClass: {
      type: [Object, null],
      default: null,
    },
    searchPlaceholder: {
      type: String,
      default: 'Search',
    },
    tableTitle: {
      type: String,
      default: '',
    },
    showSearch: {
      type: Boolean,
      default: true,
    },
    showTableTop: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      filterToggle: false,
    }
  },
  computed: {
    headSlotNames() {
      return this.tableColumns.map(item => `head(${item.key})`)
    },
    cellSlotNames() {
      return this.tableColumns.map(item => `cell(${item.key})`)
    },
    tableList() {
      return this.tableListArray
    },
  },
  methods: {
    getTableProps() {
      const defaultTableProps = {
        ref: `${this.moduleName}Table`,
        items: this.list,
        responsive: true,
        fields: this.tableColumns,
        'primary-key': 'id',
        'show-empty': true,
        'empty-text': this.$t('No matching records found'),
        busy: this.isBusy,
        class: 'position-relative',
      }
      return {
        ...defaultTableProps,
        ...this.tableProps,
      }
    },
    onSearch(searchQuery) {
      this.search(searchQuery, this)
    },
    search: debounce((searchQuery, ctx) => {
      ctx.reloadData()
    }, 500),
    toggleFilter() {
      this.filterToggle = !this.filterToggle
    },
    onFilter(filter) {
      // eslint-disable-next-line no-restricted-syntax,guard-for-in
      for (const field in filter) {
        this.extraParams[field] = filter[field]
      }
      this.refetchData()
    },
    onGenerateExel(filter, endpoint) {
      axios.get(`${endpoint}`, { params: this.filterClass.convertToQuery(this.filters) }).then(response => {
        const link = document.createElement('a')
        link.href = response.data.path
        link.setAttribute('download', true)
        document.body.appendChild(link)
        link.click()
      })
      this.refetchData()
    },
    reloadData() {
      this.filterToggle = false
      this.refetchData()
    },
    searchQueryClear() {
      this.searchQuery = ''
      this.reloadData()
    },
  },
  setup(props) {
    const { filterClass } = props
    if (filterClass && filterClass.filterComponent) {
      Vue.component(`${props.moduleName}FilterComponent`, () => filterClass.filterComponent)
    }
    return {
      ...tableConfig(props.moduleName, props.tableConfigOptions, props.filterClass),
    }
  },
}
</script>
